import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { toast } from '../components/Toasts.jsx';

// T161 — extracted verbatim from DashboardPage (which was 2614 lines / 35
// useState). Owns the two async scan flows (full "Scan now" + IMAP LinkedIn-
// alert pull) and their polling lifecycle. `refresh` is the dashboard's data
// re-fetch, injected so the hook can pull fresh stats/recs when a scan lands.
//
// Behaviour is unchanged: same 202-then-poll loop, same toasts, same 5-minute
// spinner ceiling, same unmount cleanup. Returns the state the page renders
// plus the two trigger functions the buttons call.
// T271(6) — one polling loop.
//
// The two scan flows here each carried the same ~40 lines: the same deadline,
// the same 2s/3s cadences, the same "unmounted mid-request" guard, the same
// give-up-on-timeout branch. They were not identical, either — a difference in
// a retry interval between two copies of one loop is a bug waiting for the day
// somebody fixes only one of them. T266 was about to make a third copy inside
// the onboarding wizard; this is the loop it should have called.
//
// Terminal statuses come from the caller because the two endpoints agree on
// them today and there is no guarantee a third will.
//
// @param fetchOnce  () => Promise<scan>
// @param isMounted  () => boolean — stop touching state after unmount
// @param onSettled  (scan) => void, exactly once, on completed | failed
// @param onTick     (scan) => void for each non-terminal poll (live progress)
// @param onTimeout  (errOrNull) => void once the deadline passes
// @param setTimer   (id) => void so the caller can clear it on unmount
export function pollUntilTerminal({
  fetchOnce,
  isMounted,
  onSettled,
  onTick,
  onTimeout,
  setTimer,
  maxMs = 5 * 60 * 1000,
  firstDelayMs = 1500,
  intervalMs = 2000,
  retryMs = 3000,
  now = () => Date.now(),
}) {
  const startedAt = now();
  const tick = async () => {
    try {
      const scan = await fetchOnce();
      if (!isMounted()) return;
      if (scan?.status === 'completed' || scan?.status === 'failed') {
        await onSettled(scan);
        return;
      }
      if (now() - startedAt > maxMs) {
        onTimeout(null);
        return;
      }
      onTick?.(scan);
      setTimer(setTimeout(tick, intervalMs));
    } catch (e) {
      if (!isMounted()) return;
      // A transient failure is not the end of the scan — the job keeps running
      // server-side. Keep trying until the deadline, then say so.
      if (now() - startedAt > maxMs) {
        onTimeout(e);
        return;
      }
      setTimer(setTimeout(tick, retryMs));
    }
  };
  setTimer(setTimeout(tick, firstDelayMs));
}

/**
 * T375 — what a "Fetch mail" run produced, in words.
 *
 * The operator pressed the button after receiving a reply and was told "no new
 * jobs", because the message only ever counted the LinkedIn half — which on his
 * mailbox is usually zero. The employer half is what he was waiting for, and it
 * was invisible.
 *
 * Exported and pure so the wording is tested against THIS function rather than
 * against a copy of it in the test file.
 */
export function imapScanSummary(scan = {}) {
  const newJobs = scan.jobsCreated ?? 0;
  const newMail = scan.employerMailNew ?? 0;
  const proposals = scan.employerProposals ?? 0;
  const scheduled = scan.employerScheduled ?? 0;

  // T387 — three of these four numbers are what THIS run produced. The fourth
  // is not, and saying them in one breath told him something untrue.
  //
  // `jobsCreated`, `employerMailNew` and `employerScheduled` are per-run
  // deltas. `employerProposals` is `countProposals(userId)` — every unresolved
  // proposal in the pool, whatever run produced it. So a fetch that pulled
  // nothing at all reported "Inbox read · 2 stage changes to approve", and he
  // reasonably read that as "your new mail produced two suggestions" and went
  // looking for them. The two were dated five weeks and two months earlier.
  //
  // So: the run's own output first, and the standing backlog said separately,
  // as the standing thing it is.
  const parts = [];
  if (newJobs > 0) parts.push(`${newJobs} new job${newJobs === 1 ? '' : 's'}`);
  if (newMail > 0) parts.push(`${newMail} new repl${newMail === 1 ? 'y' : 'ies'}`);
  if (scheduled > 0) parts.push(`${scheduled} interview${scheduled === 1 ? '' : 's'} added to Tasks`);

  const produced = parts.length > 0;
  const head = produced ? `Inbox read · ${parts.join(' · ')}` : 'Inbox read · nothing new';
  // "still waiting", not "to approve": the wording has to survive being read
  // after a run that produced nothing, which is most of them.
  const tail = proposals > 0 ? ` · ${proposals} stage change${proposals === 1 ? '' : 's'} still waiting` : '';

  return {
    // `anything` drives the toast's tone. A backlog is not an achievement, so
    // a run that produced nothing stays neutral even when the backlog is not
    // empty — otherwise every fetch forever reads as a success.
    anything: produced,
    text: `${head}${tail}`,
  };
}

export function useScans({ refresh }) {
  const [scanning, setScanning] = useState(false);
  const [imapScanning, setImapScanning] = useState(false); // T029
  const [scanResult, setScanResult] = useState(null);
  const [scanProgress, setScanProgress] = useState(null); // T113 — latest non-terminal poll snapshot

  const scanPollRef = useRef(null); // T101 — active poll's setTimeout id (cleared on unmount)
  const imapPollRef = useRef(null); // T147 — active IMAP-scan poll's setTimeout id
  const mountedRef = useRef(true); // T101 — guards setState after an in-flight poll resolves post-unmount

  // T101 — stop any in-flight scan poll when the dashboard unmounts. Clearing
  // the timer cancels a SCHEDULED poll; mountedRef guards the case where a poll
  // is already awaiting its fetch when we unmount (the timer already fired).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (scanPollRef.current) clearTimeout(scanPollRef.current);
      if (imapPollRef.current) clearTimeout(imapPollRef.current);
    };
  }, []);

  // T147 — the IMAP scan is async now (was a synchronous handler that timed out
  // at the Apache proxy on a back-filled inbox). POST enqueues a BullMQ job and
  // returns 202 + a scanId; we poll GET /scans/imap/:id until completed/failed.
  const triggerImapScan = async () => {
    if (imapScanning) return; // re-entry guard (keyboard / command palette paths)
    if (imapPollRef.current) clearTimeout(imapPollRef.current);
    setImapScanning(true);
    toast('Reading your inbox…', { kind: 'info' });

    let scanId;
    try {
      const body = await api.post('/scans/imap-linkedin');
      scanId = body.scanId;
      if (!scanId) throw new Error('IMAP scan did not return an id');
      if (body.alreadyRunning) toast('An inbox scan is already running — watching it…', { kind: 'info' });
    } catch (e) {
      const code = e.body?.error;
      let msg = e.body?.message || e.message;
      if (code === 'imap_not_configured') msg = 'Connect your email inbox in Settings → Connectors first.';
      else if (code === 'anthropic_key_missing') msg = 'Set your Anthropic API key in Settings → Connectors first.';
      toast(msg, { kind: 'error' });
      setImapScanning(false);
      return;
    }

    pollUntilTerminal({
      fetchOnce: () => api.get(`/scans/imap/${scanId}`),
      isMounted: () => mountedRef.current,
      setTimer: (id) => { imapPollRef.current = id; },
      onSettled: async (scan) => {
        setImapScanning(false);
        if (scan.status === 'failed') {
          const code = scan.fatalError;
          let msg = 'Inbox scan failed.';
          if (code === 'imap_not_configured') msg = 'Connect your email inbox in Settings → Connectors first.';
          else if (code === 'imap_connect_failed') msg = 'Inbox connection failed — check the email settings.';
          else if (code === 'imap_timeout') msg = 'Reading your inbox timed out — try again.';
          else if (code === 'imap_password_decrypt_failed') msg = 'Saved inbox password could not be read — re-enter it in Settings.';
          else if (code) msg = code;
          toast(msg, { kind: 'error' });
          return;
        }
        const summary = imapScanSummary(scan);
        toast(summary.text, { kind: summary.anything ? 'success' : 'info' });
        await refresh();
      },
      onTimeout: (e) => {
        setImapScanning(false);
        toast(
          e ? (e.body?.message || e.message || 'Lost track of the inbox scan')
            : 'Inbox scan is taking longer than usual — it keeps running. Refresh in a bit.',
          { kind: e ? 'error' : 'info' },
        );
      },
    });
  };

  // T101 — "Scan now" is async. POST /scans/trigger enqueues a BullMQ job
  // and returns 202 with a scanId; we then poll GET /scans/:id until the
  // row reads completed/failed. The button + ScanPanel stay in their
  // "scanning…" state for the whole poll so live progress keeps showing.
  const notifyScanDone = (scan) => {
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted' &&
      localStorage.getItem('hunter-notifications') === 'on'
    ) {
      const n = new Notification('hunter — scan done', {
        body: `${scan.totalJobsNew} new / ${scan.totalJobsFound} matched / ${scan.totalJobsRaw} raw · ${scan.durationMs}ms`,
        tag: 'hunter-scan',
      });
      // T132 — clicking the OS notification focuses the tab + opens the Activity panel.
      n.onclick = () => {
        window.focus();
        window.dispatchEvent(new CustomEvent('hunter:open-activity'));
      };
    }
  };

  const triggerScan = async () => {
    if (scanning) return; // T113 — hard re-entry guard (covers keyboard/programmatic paths, not just the disabled button)
    if (scanPollRef.current) clearTimeout(scanPollRef.current);
    setScanning(true);
    setScanResult(null);
    setScanProgress(null);
    toast('Scan queued — fanning out across sources…', { kind: 'info' }); // T113 — immediate ack before the 202

    let scanId;
    try {
      const body = await api.post('/scans/trigger');
      scanId = body.scanId;
      if (!scanId) throw new Error('Scan did not return an id');
      if (body.alreadyRunning) {
        toast('A scan is already running — watching it…', { kind: 'info' });
      }
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
      setScanning(false);
      return;
    }

    // Poll until terminal. The embedding pass can be slow on a cold model,
    // so allow up to ~5 min before we release the spinner (the job keeps
    // running server-side regardless).
    pollUntilTerminal({
      fetchOnce: () => api.get(`/scans/${scanId}`),
      isMounted: () => mountedRef.current,
      setTimer: (id) => { scanPollRef.current = id; },
      onTick: (scan) => setScanProgress(scan), // T113 — live snapshot for the panel
      onSettled: async (scan) => {
        setScanResult(scan);
        setScanProgress(null);
        setScanning(false);
        await refresh(); // pull updated stats + recs
        if (!mountedRef.current) return;
        if (scan.status === 'failed') toast(scan.fatalError || 'Scan failed', { kind: 'error' });
        else notifyScanDone(scan);
      },
      onTimeout: (e) => {
        setScanning(false);
        toast(
          e ? (e.body?.message || e.message || 'Lost track of the scan')
            : 'Scan is taking longer than usual — it keeps running. Refresh in a bit.',
          { kind: e ? 'error' : 'info' },
        );
      },
    });
  };

  return { scanning, imapScanning, scanResult, scanProgress, triggerScan, triggerImapScan };
}
